import { Component, Inject, Injector, OnInit } from '@angular/core';
import { TableColumn } from '@models/tableColumn';
import { PricingsService } from '@services/entity/pricings.service';
import { StripeInfosService } from '@services/entity/stripe-infos.service';
import { plainToClass } from 'class-transformer';
import { TableOptions } from '@models/tableOptions';
import { UsersService } from '@services/entity/users.service';
import { InvoiceDto } from '@dto/invoiceDto';
import { InvoicesService } from '@services/entity/invoices.service';
import { UserDto } from '@dto/userDto';
import { TableFunctionsService } from '@services/table/table-functions.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TransactionsService } from '@services/entity/transactions.service';
import { TransactionDto } from '@dto/transactionDto';
import { DatePipe } from '@angular/common';
import { AuthenticationService } from '@services/authentication/authentication.service';
import { Subscription } from 'rxjs';
import { EventService } from '@services/event/event.service';
import { KwhPipe } from 'src/app/pipes/kwh.pipe';
import { CurrencyPipe } from '@angular/common';
import { CustomersService } from '@services/entity/customers.service';
import { InvoiceItemDto } from '@dto/invoiceItemDto';

@Component({
  selector: 'app-open-invoices-table',
  templateUrl: './open-invoices-table.component.html',
  styleUrls: ['./open-invoices-table.component.scss'],
})
export class OpenInvoicesTableComponent implements OnInit {

  public className = 'OpenInvoicesTableComponent'

  public entities: {}[] = []
  public isOperatorView: boolean
  public hasStripeAccount: boolean
  public isTableReady: boolean = false

  public isUser: boolean = false
  public isCustomer: boolean = false
  public isOperator: boolean = false

  public ownerUserId: string
  public ownerCustomerId: number

  public invoices: InvoiceDto[] = []
  public user: UserDto
  public users: UserDto[] = []
  public invoiceItemsToBeBilled: InvoiceItemDto[] = []

  public tableColumns: TableColumn[] = [
    { columnDescription: `Benutzer`, property: 'recipientFullName', options: {
      tdStyleClasses: 'createdDate' } },
    
    { columnDescription: `Ladevorgänge`, property: 'selectedTransactions', options: {
      tdStyleClasses: 'createdDate', isLength: true } },

    { columnDescription: `Summe kWh geladen`, property: 'meterAmount', options: {
      tdStyleClasses: 'meterAmount', columnPipe: { pipe: new KwhPipe } } },

    { columnDescription: `Offener Betrag`, property: 'amount', options: {
      tdStyleClasses: 'amount', columnPipe: { pipe: new CurrencyPipe('de-DE', 'EUR') } } },
  ];
  
  @Inject(EventService)
  public eventService: EventService;
  public routerSubscription: Subscription;

  constructor(
    public tableFunctions: TableFunctionsService,
    public router: Router,
    public datePipe: DatePipe,
    public activatedRoute: ActivatedRoute,
    public transactionsService: TransactionsService,
    public usersService: UsersService,
    public customersService: CustomersService,
    public pricingsService: PricingsService,
    public invoicesService: InvoicesService,
    public authenticationService: AuthenticationService,
    public stripeInfosService: StripeInfosService,
    injector: Injector
  ) {
    this.router = injector.get(Router);
    this.eventService = injector.get(EventService);
  }

  ngOnInit(): void {
    this.isOperatorView = this.activatedRoute.snapshot.data['isOperatorView'];
    this.getRoles();

    if (this.isOperator) {
      this.ownerCustomerId = this.activatedRoute.parent.snapshot.params.key;
    } else {
      this.ownerCustomerId = localStorage.getItem('customerId') ? parseInt(localStorage.getItem('customerId')) : undefined;
    }

    this.getUser();
    
    this.tableFunctions.sortColumn = 'index';
    this.tableFunctions.sortDirection = 'desc';
  }

  ngAfterViewInit() {
    // this.getInvoices(false);
  }

  getUser() {
    if(this.isOperator) {
      this.customersService.getMasterUserOfCustomer(this.ownerCustomerId).subscribe(masterUser => {
        this.usersService.getUserDtoById(masterUser.id).subscribe(user => {
          this.user = plainToClass(UserDto, user);
          this.getInvoices();
          this.getInvoiceItems();
        })
      })
    } else {
      this.usersService.getLoggedInUser().subscribe(user => {
        this.user = plainToClass(UserDto, user);
        this.getInvoices();
        this.getInvoiceItems();
      })
    }
  }

  async getInvoices() {
    if (!this.user || !this.user.id) return false
    this.invoicesService.getInvoicesOfCustomer(this.user.id).subscribe(invoices => {
      this.invoices = plainToClass(InvoiceDto, invoices);
    }, error => console.error(error))
  }
  
  async getInvoiceItems() {
    if (this.user.id == undefined) return false
      this.invoicesService.getAllUnbilledInvoiceItemsOfIssuerForManualBillingAndAutoBilling(this.user.id).subscribe(invoiceItems => {
        this.invoiceItemsToBeBilled = plainToClass(InvoiceItemDto, invoiceItems);
       this.getUsersToBeBilled()
    })
  }

  async getUsersToBeBilled() {
    const groupedItems = this.invoiceItemsToBeBilled.reduce((acc, item) => {
      if (!acc[item.recipientUserId]) {
        acc[item.recipientUserId] = [];
      }
      acc[item.recipientUserId].push(item);
      return acc;
    }, {} as Record<string, InvoiceItemDto[]>);


    for (const [userId, items] of Object.entries(groupedItems)) {
      if (items.length > 0) {
        this.entities.push({
          userId: userId,
          recipientFullName: items[0].recipientUserFullName,
          selectedTransactions: items.length,
          meterAmount: (this.getInvoiceMeterAmountOfRecipient(items) / 1000).toLocaleString('de-DE', { minimumFractionDigits: 3 }),
          amount: this.getInvoiceAmountOfRecipient(items).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }),
        })
      }
    }
    this.isTableReady = true
  }

  getInvoiceMeterAmountOfRecipient(items: InvoiceItemDto[]) {
    if (items.length < 1) return 0
    return items.map(ii => ii.quantity).reduce((previousValue, currentValue) => previousValue + currentValue, 0)
  }
  
  getInvoiceAmountOfRecipient(items: InvoiceItemDto[]) {
    if (items.length < 1) return 0
    return items.map(ii => ii.total).reduce((previousValue, currentValue) => previousValue + currentValue, 0)
  }

  getRoles() {
    if (this.authenticationService.userHasRole('Operator')) this.isOperator = true;
    if (this.authenticationService.userHasRole('Customer')) this.isCustomer = true;
    if (this.authenticationService.userHasRole('User')) this.isUser = true;
  }

  isDate(value: string) {
    if (!value || typeof value != 'string') return false
    return new RegExp(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z*/).test(value.trim())
  }

  parseDate(date: Date) {
    if (!date) return false
    const months = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember']
    return months[new Date(date).getMonth()]
  }

  matches(entity: InvoiceDto, term: string) {
    return InvoicesService.matches(entity, term)
  }
}